import React, { useEffect, useState } from 'react';
import { downloadFileAs } from '../../utils/downloadFile';
import {
  fetchEmployeeCvDocuments,
  groupCvDocuments,
  getMissingRequiredDocuments,
} from '../../utils/cvDocuments';
import {
  FaTimes,
  FaFolderOpen,
  FaFilePdf,
  FaFileWord,
  FaFile,
  FaEye,
  FaDownload,
  FaExclamationCircle,
  FaExclamationTriangle,
} from 'react-icons/fa';
import styles from './EmployeeDocumentsModal.module.css';

// Çalışan Yönetimi'ndeki "CV/GCP/IATA Belgeleri" bölümüyle aynı kaynak ve
// aynı sıra (bkz. utils/cvDocuments). SMO tarafında yalnızca görüntüleme ve
// indirme var; yükleme ve silme yok.
const fileIcon = (fileType = '') => {
  const type = String(fileType).toLowerCase();
  if (type.includes('pdf')) return <FaFilePdf className={styles.iconPdf} />;
  if (type.includes('doc') || type.includes('word')) return <FaFileWord className={styles.iconWord} />;
  return <FaFile className={styles.iconGeneric} />;
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size === 0) return '';
  return `${(size / 1024).toFixed(2)} KB`;
};

const EmployeeDocumentsModal = ({ show, onHide, employee, isDarkMode = false }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!show || !employee?.id) return;

    let cancelled = false;
    const fetchDocuments = async () => {
      setLoading(true);
      setError(false);
      try {
        const docs = await fetchEmployeeCvDocuments(employee.id);
        if (!cancelled) setDocuments(docs);
      } catch (err) {
        console.error('CV/GCP/IATA belgeleri yüklenemedi:', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDocuments();
    return () => {
      cancelled = true;
    };
  }, [show, employee?.id]);

  if (!show) return null;

  const groups = groupCvDocuments(documents);
  const missingRequired = getMissingRequiredDocuments(documents);

  return (
    <div
      className={`${styles.overlay} ${isDarkMode ? styles.dark : ''}`}
      onClick={onHide}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <FaFolderOpen className={styles.headerIcon} />
            <div>
              <h2>CV / GCP / IATA Belgeleri</h2>
              <span className={styles.headerSubtitle}>
                {employee ? `${employee.name || ''} ${employee.surname || ''}`.trim() : ''}
              </span>
            </div>
          </div>
          <button type="button" className={styles.closeButton} onClick={onHide} aria-label="Kapat">
            <FaTimes />
          </button>
        </div>

        <div className={styles.body}>
          {/* Hic yuklenmemis zorunlu belgeler tek satirda hatirlatilir */}
          {!loading && !error && missingRequired.length > 0 && (
            <div className={styles.missingNotice}>
              <FaExclamationTriangle />
              <span>
                Eksik zorunlu belge: {missingRequired.map((type) => type.label).join(', ')}
              </span>
            </div>
          )}
          {loading ? (
            <div className={styles.stateBox}>
              <span className={styles.spinner} />
              <p>Belgeler yükleniyor...</p>
            </div>
          ) : error ? (
            <div className={styles.stateBox}>
              <FaExclamationCircle className={styles.stateIcon} />
              <p>Belgeler yüklenirken bir hata oluştu.</p>
            </div>
          ) : groups.length === 0 ? (
            <div className={styles.stateBox}>
              <FaFolderOpen className={styles.stateIcon} />
              <p>Bu çalışanın CV/GCP/IATA belgesi bulunmuyor.</p>
            </div>
          ) : (
            groups.map((group) => (
              <section key={group.key} className={styles.group}>
                <h3 className={styles.groupTitle}>
                  {group.label}
                  <span className={styles.groupCount}>{group.docs.length}</span>
                </h3>
                {group.docs.map((doc) => (
                  <div key={doc.id} className={styles.documentItem}>
                    <span className={styles.documentIcon}>{fileIcon(doc.fileType)}</span>
                    <div className={styles.documentInfo}>
                      <span className={styles.documentName} title={doc.fileName}>
                        {doc.fileName}
                      </span>
                      <span className={styles.documentMeta}>
                        {[formatDate(doc.uploadedAt), formatSize(doc.fileSize)]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </div>
                    <div className={styles.documentActions}>
                      <a
                        href={doc.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.actionButton}
                        title="Görüntüle"
                      >
                        <FaEye />
                      </a>
                      <button
                        type="button"
                        className={styles.actionButton}
                        onClick={() => downloadFileAs(doc.fileUrl, doc.fileName)}
                        title="İndir"
                      >
                        <FaDownload />
                      </button>
                    </div>
                  </div>
                ))}
              </section>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeDocumentsModal;
